import {
    useEffect,
    useReducer,
    lazy,
    Suspense,
    useState,
    useCallback,
} from 'react';
import { withTranslation } from 'react-i18next';
import MobileLayoutProvider from '../../contexts/mobile-context/MobileContext';
import {
    BrowserRouter,
    Switch,
    Route,
    useHistory,
    Redirect,
    useLocation
} from 'react-router-dom';

import { initRequestManagerWebLogin, logout } from '../../api/managers/requestManager';
import { getInstallerCustomers } from '../../api/customers';

import useWhiteLabel from '../../hooks/useWhiteLabel';

import host, { isDemo, isMobileApp } from '../../utils/getHost';
import { getDeviceType, getOSName } from '../../utils/browser';
import { alphabeticalOrderComparison } from '../../utils/string';
import { clearLoggedInRedirectInSessionStorage, getLoggedInRedirectInSessionStorage } from '../../utils/manageStorage';
import { TURQUOISE, WHITE } from '../../utils/colours';
import { DEMO_PATH } from '../../utils/demo';

import '../../translations/i18n'; // Translations - needs to be bundled
import routes, {
    LAYOUT_HEADER_FOOTER,
    LAYOUT_FULLSCREEN,
    LAYOUT_NONE,
    getRoutesWithLayout,
    LAYOUT_APP,
    getRouteByPath,
    canUserAccessRoute
} from '../../pages/routes';
import translationKeys from '../../translations/keys';

import ErrorBoundary from '../error-boundary';
import WhiteLabel from '../white-label';
import Loading from '../loading';
import ScrollToTop from '../scroll-to-top';
import ErrorPage from '../../pages/error';
import ErrorScreen from '../error-screen';
import {
    AppLayout,
    FullScreenLayout,
    HeaderFooterLayout,
} from '../layout-managers';

import NetworkError from './NetworkError';
import DemoApp from './DemoApp';
import MobileNoConnectivity from './MobileNoConnectivity';
import { clearCameraThumbTimes } from '../../utils/thumbStore';
import { MANAGE_PORTAL_PATH, redirectToManagePortal } from '../../pages/partner-portal/redirectToManagePortal';

// 404 page
// All other pages come from imported 'routes' object above
const NotFound = lazy(() => import('../../pages/404'));

const isCCTVConnect = /cctvconnect\.com/i.test(host);
const isAndroidMobileDevice = (
    getOSName() === 'Android'
    && ['mobile', 'tablet'].includes(getDeviceType())
);

export const sessionId = `${Date.now()}-${Math.random().toString(36).slice(2, 2 + 9)}`;

// Account data managed using a reducer
const reducer = (state, action) => {
    switch (action.type) {
        case CHANGE_EMAIL:
            return { ...state, email: action.payload };
        case LOGIN:
            return action.payload;
        case REFRESH_TOKEN:
            const [loginToken, authToken, managementToken] = action.payload;

            return {
                ...state,
                authToken,
                managementToken,
                webLogin: {
                    ...state.webLogin,
                    loginToken
                }
            };
        case LOGOUT:
            return {};
        case NEW_ORGANISATION:
            return {
                ...state,
                organisations: state.organisations.concat(action.payload),
            };
        case CHANGE_OWN_ORGANISATION: {
            const { changes, userId } = action.payload;
            const targetId = userId ?? state.uid;
            const targetIdx = state.organisations.findIndex(o => o.id === targetId);
            if (targetIdx === -1) {
                return state;
            }
            return {
                ...state,
                organisations: [
                    ...state.organisations.slice(0, targetIdx),
                    {
                        ...state.organisations[targetIdx],
                        ...changes
                    },
                    ...state.organisations.slice(targetIdx + 1)
                ]
            };
        }
        case NEW_CAMERA_GROUP:
            return {
                ...state,
                cameraGroups: state.cameraGroups.concat(action.payload).sort(({ name: a }, { name: b }) => alphabeticalOrderComparison(a, b))
            };
        case DELETE_CAMERA_GROUP:
            return {
                ...state,
                cameraGroups: state.cameraGroups.filter(({ id }) => id !== action.payload)
            };
        case EDIT_CAMERA_GROUP:
            const editedGroup = action.payload;
            return {
                ...state,
                cameraGroups: state.cameraGroups.map(group => group.id === editedGroup.id ? editedGroup : group)
            };
        case COMPLETE_SELF_REG_STEP:
            // Completed step is in payload
            return {
                ...state,
                incompleteSelfRegSteps: state.incompleteSelfRegSteps?.filter(step => step !== action.payload)
            };
        default:
            throw new Error();
    }
};

// Actions for reducer
const CHANGE_EMAIL = 'CHANGE_EMAIL';
const LOGIN = 'LOGIN';
const LOGOUT = 'LOGOUT';
const REFRESH_TOKEN = 'REFRESH_TOKEN';
const NEW_ORGANISATION = 'NEW_ORGANISATION';
const CHANGE_OWN_ORGANISATION = 'CHANGE_OWN_ORGANISATION';
const NEW_CAMERA_GROUP = 'NEW_CAMERA_GROUP';
const DELETE_CAMERA_GROUP = 'DELETE_CAMERA_GROUP';
const EDIT_CAMERA_GROUP = 'EDIT_CAMERA_GROUP';
const COMPLETE_SELF_REG_STEP = 'COMPLETE_SELF_REG_STEP';

// Chooses between normal and demo version of app
const AppRouter = () => {

    return (
        <BrowserRouter
            // All demo routes need to be prefixed with /demo
            // Note this means we cannot use React Router to link from demo app to normal app and vice versa
            basename={isDemo ? DEMO_PATH : undefined}
        >
            <MobileLayoutProvider>
                {/* Code common to both here - currently only `ScrollToTop` */}
                <ScrollToTop />

                {/* Render app */}
                {
                    isDemo ? (
                        <DemoApp />
                    ) : (
                        <App />
                    )
                }
            </MobileLayoutProvider>
        </BrowserRouter>
    );
}

const useAndroidKeyboardScroll = () => {
    useEffect(() => {
        if (!isAndroidMobileDevice) {
            return undefined;
        }

        let timeoutId;
        const handleFocus = (e) => {
            if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
                timeoutId = setTimeout(() => {
                    e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }, 200);
            }
        };
        document.addEventListener('focusin', handleFocus);
        return () => {
            clearTimeout(timeoutId);
            document.removeEventListener('focusin', handleFocus);
        };
    }, []);
};

// Main app version
// Only exported for tests
export const App = withTranslation()(({ tReady, t }) => {

    useAndroidKeyboardScroll();
    // Account data
    // Only property is loading: true until data has been fetched
    // We know account data has been received once (among other properties) accountData.authToken exists
    const [accountData, dispatch] = useReducer(reducer, { loading: true });

    // Whether a network error has prevented a token refresh
    // If true, we show the `NetworkError` page
    // Once a network error has occurred, this page will keep checking the connection and reload the app when it returns
    const [networkError, setNetworkError] = useState(false);
    // Track browser offline state separately
    const [isOffline, setIsOffline] = useState(!navigator.onLine);
    
    const handleNetworkError = useCallback(() => {
        setNetworkError(true);
    }, []);

    const history = useHistory();

    // Functions for managing account data
    const handleChangeEmail = useCallback(newEmail => dispatch({ type: CHANGE_EMAIL, payload: newEmail }), []);
    const handleLogin = useCallback(accountData => {

        // Add preset group(s)
        // Preset groups have ids <= 0
        // For preset group names we just use the translation key as translations may not have been loaded yet
        // These get translated before being passed to AppLayout layout manager

        // If installer and they have some cameras, add "Free partner demo" group
        if (accountData.managementType === 'i') {
            const installerOrg = accountData.organisations.find(({ id }) => id === accountData.uid);
            if (installerOrg && installerOrg.cameras) {
                accountData.cameraGroups.unshift({
                    id: -1,
                    name: translationKeys.installers.FREE_PARTNER_DEMO,
                    cloudStorageDuration: installerOrg.cloudStorageDuration,
                    cameras: accountData.partnerDemoCameras
                });
            }
        }
        
        dispatch({ type: LOGIN, payload: accountData });
    }, []);
    const handleLogout = useCallback(() => {
        dispatch({ type: LOGOUT });
    }, []);
    const handleNewOrganisation = useCallback(newOrganisation => {
        dispatch({ type: NEW_ORGANISATION, payload: newOrganisation });
    }, []);
    const handleChangeOrganisation = useCallback((changes, userId) => {
        dispatch({ type: CHANGE_OWN_ORGANISATION, payload: { changes, userId } });
    }, []);
    const handleNewCameraGroup = useCallback(newCameraGroup => {
        dispatch({ type: NEW_CAMERA_GROUP, payload: newCameraGroup });
    }, []);
    const handleDeleteCameraGroup = useCallback(groupId => {
        dispatch({ type: DELETE_CAMERA_GROUP, payload: groupId });
    }, []);
    const handleEditCameraGroup = useCallback(editedGroup => {
        dispatch({ type: EDIT_CAMERA_GROUP, payload: editedGroup });
    }, []);
    const handleCompleteSelfRegStep = useCallback(completedStep => {
        dispatch({ type: COMPLETE_SELF_REG_STEP, payload: completedStep });
    }, []);


    // Runs once when App first loaded
    // Logs in using data in local storage if possible
    useEffect(() => {
        const initialLogin = async () => {
            try {
                handleLogin(await initRequestManagerWebLogin(handleLogout, handleNetworkError));
            } catch (error) {
                // console.error(error);
                handleLogout();
            }
        }
        initialLogin();
    }, [handleNetworkError, handleLogin, handleLogout]);

    // Add network status listeners for offline detection
    useEffect(() => {
        const handleOnline = () => {
            setIsOffline(false);
        };
        const handleOffline = () => {
            setIsOffline(true);
        };
        
        window.addEventListener('online', () => {
            console.log('Received online event from browser');
            handleOnline();
        });
        window.addEventListener('offline', () => {
            console.log('Received offline event from browser');
            handleOffline();
        });
        
        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, []);

    
    // White label custom hook
    const [whiteLabelHostInfo, installerCCTVCBranding] = useWhiteLabel(
        host,
        isCCTVConnect,
        accountData.installerid
    );

    // Passed down to sign out buttons
    const logoutAction = useCallback(async () => {
        // No cb (for useAsyncButtonAction) as we leave page

        // Browser may cancel request if we leave page without waiting for request
        // Thus we wait for it to finish to ensure login token series is deleted
        await logout();
        clearCameraThumbTimes();

        // Go to white label's return link if it exists (and we're not on localhost as that's really annoying otherwise)
        if (
            whiteLabelHostInfo?.returnLink &&
            !window.location.hostname.match(/localhost/)
        ) {
            window.location.assign(whiteLabelHostInfo.returnLink);
        } else {
            // Otherwise back to sign in page
            handleLogout();
            history.push(routes.signIn.path);
        }
    }, [handleLogout, history, whiteLabelHostInfo?.returnLink]);

    const isSignedIn = !!accountData.uid;

    // Function which takes a page's config object (as defined in src/pages/routes.js) and renders its component within a Route component
    const renderComponentWithRoute = ({
        path,
        allPaths,
        renderComponent,
        routes: subroutes,
        ...routeConfig
    }) => {
        // Passes required args to given renderComponent function
        // These args include account data, functions for modifying account data and flag indicating whether web app is being accessed through mobile app
        const renderComponentWithArgs = (renderComponent) =>
            renderComponent(
                accountData,
                {
                    // Functions for modifying account data
                    handleChangeEmail,
                    handleLogin,
                    handleLogout,
                    handleNetworkError,
                    handleNewOrganisation,
                    handleChangeOrganisation,
                    handleNewCameraGroup,
                    handleDeleteCameraGroup,
                    handleEditCameraGroup,
                    handleCompleteSelfRegStep,

                    // Also include logoutAction
                    logoutAction
                }
            );

        // Function to generate component
        const component = () => {
            // If there are subroutes and no renderComponent function, then we need to route user rather than just render a component
            if (!renderComponent && subroutes) {
                return (
                    <Switch>
                        {/* Redirect from base route to first subroute */}
                        <Redirect
                            exact
                            from={path}
                            to={Object.values(subroutes)[0].path}
                        />

                        {/* Subroutes */}
                        {Object.values(subroutes).map(
                            ({ allPaths, path, renderComponent }) => (
                                <Route exact path={allPaths} key={path}>
                                    {renderComponentWithArgs(renderComponent)}
                                </Route>
                            )
                        )}
                    </Switch>
                );
            }

            // Run function from routes config to generate component
            return renderComponentWithArgs(renderComponent);
        };


        return (
            <Route
                // Path needs to include all paths that could go to this page, including subroutes
                // Then we can use the 'exact' Route prop and all requests to a URL not in pages/routes.js will go to 404 page
                path={getAllPathsThatMeetAccessCondition({ routes: subroutes, path, allPaths, ...routeConfig }, accountData)}
                key={path}
                exact
            >
                {component()}
            </Route>
        );
    };

    // Render
    if (
        // Initial loading includes translations, white label info and user account data
        tReady &&
        !accountData.loading &&
        whiteLabelHostInfo &&
        // Either this isn't CCTVC so we don't need installer branding
        // Or this is CCTVC and we have installer branding
        // Or this is CCTVC but as we don't have an installer id (e.g. we're not logged in) we can't look up installer branding yet
        (!isCCTVConnect ||
            installerCCTVCBranding ||
            !accountData.installerid)
    ) {
        // Only render app once user data and white label info has been received
        return (
            <ErrorBoundary
                errorComponent={ErrorScreen}
                message={t(translationKeys.errors.APP_ERROR)}
            >
                <WhiteLabel
                    info={
                        isCCTVConnect
                            ? {
                                  ...whiteLabelHostInfo,
                                  ...installerCCTVCBranding,
                              } // Overwrite with installer specific branding if CCTVC
                            : whiteLabelHostInfo
                    }
                >
                    <Suspense
                        // Too many spinners on mobile app (as sidebar, etc. are hidden)
                        fallback={isMobileApp ? null : <Loading fill />}
                    >
                        <ErrorBoundary
                            errorComponent={ErrorPage}
                            message={t(translationKeys.errors.PAGE_ERROR)}
                        >
                            {
                                (networkError || isOffline) ? (
                                    isMobileApp ? (
                                        <MobileNoConnectivity />
                                    ) : (
                                        <NetworkError />
                                    )
                                ) : (
                                    <>
                                    <Switch>
                                        {/* Root goes to sign in */}
                                        <Redirect
                                            exact
                                            from="/"
                                            to={routes.signIn.path}
                                        />

                                        {/* If user signed in, redirect away from all pages that are only for non-logged in users */}
                                        {isSignedIn && (
                                            <Route
                                                exact
                                                path={Object.values(routes).reduce(
                                                    (
                                                        acc,
                                                        { redirectIfLoggedIn, allPaths }
                                                    ) =>
                                                        redirectIfLoggedIn
                                                            ? acc.concat(allPaths)
                                                            : acc,
                                                    []
                                                )}
                                            >
                                                <LoggedInRedirect
                                                    accountType={accountData.accountType}
                                                />
                                            </Route>
                                        )}

                                        {/*
                                            If user not signed in, redirect to sign in page from all pages with restricted access.
                                            Have just wrapped those pages in <PrivateRoute> components before but better to stop
                                            layout manager (e.g. AppLayout) rendering too.
                                        */}
                                        {!isSignedIn && (
                                            <Route
                                                exact
                                                path={Object.values(routes).reduce(
                                                    (
                                                        acc,
                                                        { restrictAccess, allPaths }
                                                    ) =>
                                                        restrictAccess
                                                            ? acc.concat(allPaths)
                                                            : acc,
                                                    []
                                                )}
                                                render={({ location }) => (
                                                    <Redirect
                                                        to={{
                                                            pathname:
                                                                routes.signIn.path,
                                                            state: { from: location },
                                                        }}
                                                    />
                                                )}
                                            />
                                        )}

                                        {/*
                                            If user is self reg and has not completed the sign up process, all restricted access pages
                                            should redirect to "Get Started".
                                            after self reg updates march 2023 we can ignore 'shipping' step as that will always either:
                                                - be completed before payment
                                                - never be completed because user checks 'already have a cloud adapter'
                                        */}
                                        {
                                            isSignedIn && 
                                            accountData.isSelfReg && 
                                            (accountData.incompleteSelfRegSteps?.length > 0 && accountData.incompleteSelfRegSteps?.includes('payment')) &&
                                            // Make exception for additional users
                                            // We've had a number of cases where additional users (who obviously don't want their own plan) have created
                                            // an account before being granted access (rather than creating an account through an email invite).
                                            !accountData.organisations.some(({ id }) => id !== accountData.uid) &&
                                            (
                                                <Route
                                                    exact
                                                    path={Object.values(routes).reduce(
                                                        (
                                                            acc,
                                                            { restrictAccess, allPaths }
                                                        ) =>
                                                            restrictAccess
                                                                ? acc.concat(allPaths)
                                                                : acc,
                                                        []
                                                    )}
                                                    render={() => (
                                                        <Redirect
                                                            to={{
                                                                pathname:
                                                                    routes.getStarted.path,
                                                                search: '?type=enduser'
                                                            }}
                                                        />
                                                    )}
                                                />
                                            )
                                        }

                                        {/* Pages with no defined layout */}
                                        {getRoutesWithLayout(LAYOUT_NONE).map(
                                            renderComponentWithRoute
                                        )}

                                        {/* Full screen layout pages */}
                                        <Route
                                            path={getRoutesWithLayout(LAYOUT_FULLSCREEN)
                                                .map(({ allPaths }) => allPaths)
                                                .flat()}
                                            exact
                                        >
                                            <FullScreenLayout>
                                                <Suspense
                                                    // Too many spinners on mobile app (as sidebar, etc. are hidden)
                                                    fallback={isMobileApp ? null : <Loading />}
                                                >
                                                    <Switch>
                                                        {getRoutesWithLayout(
                                                            LAYOUT_FULLSCREEN
                                                        ).map(renderComponentWithRoute)}

                                                    </Switch>
                                                </Suspense>
                                            </FullScreenLayout>
                                        </Route>

                                        {/* Header/footer and app layout pages */}
                                        {/*
                                            If user signed in, we want 404 page in app layout.
                                            Otherwise we want it in header/footer layout.

                                            The layout containing the 404 page needs to be rendered last
                                            so it catches all other paths (using wildcard '*').

                                            If possible, it would be better to keep order permanent
                                            and include regex to match all routes EXCEPT those belonging
                                            to the component below. For example, app layout could be
                                            rendered first and if user logged in it could match all routes
                                            except header/footer pages.
                                        */}
                                        {
                                            // Header/footer layout without 404 page
                                            isSignedIn && (
                                                <Route
                                                    exact
                                                    path={getRoutesWithLayout(
                                                                LAYOUT_HEADER_FOOTER
                                                            )
                                                                .map(({ allPaths }) => allPaths)
                                                                .flat()
                                                        }
                                                    render={({ match }) => (
                                                        <HeaderFooterLayoutPages
                                                            match={match}
                                                            accountType={accountData.accountType}
                                                            logoutAction={logoutAction}
                                                            renderComponentWithRoute={renderComponentWithRoute}
                                                        />
                                                    )}
                                                />
                                            )
                                        }

                                        {/* App layout pages */}
                                        <Route
                                            // If user is signed in, must exclude pages that they do not have access to so they still get routed to 404 below
                                            path={getRoutesWithLayout(LAYOUT_APP)
                                                    .map(
                                                        (routeConfig) => getAllPathsThatMeetAccessCondition(
                                                                routeConfig,
                                                                accountData
                                                            )
                                                    )
                                                    .flat()
                                                    // Need to add wildcard if including 404 page
                                                    .concat(isSignedIn ? '*' : [])
                                                }
                                            render={({ match }) => (
                                                <AppLayout
                                                    routeConfig={getRouteByPath(
                                                        match.path
                                                    )}
                                                    logout={logoutAction}
                                                    accountInfo={accountData}
                                                >
                                                    <Suspense
                                                        fallback={
                                                            // Too many spinners on mobile app (as sidebar, etc. are hidden)
                                                            isMobileApp ? null : <Loading fill />
                                                        }
                                                    >
                                                        <Switch>
                                                            {getRoutesWithLayout(
                                                                LAYOUT_APP
                                                            ).map(
                                                                renderComponentWithRoute
                                                            )}
                                                            
                                                            {
                                                                // 404 page
                                                                isSignedIn && (
                                                                    <Route>
                                                                        <NotFound />
                                                                    </Route>
                                                                )
                                                            }
                                                        </Switch>
                                                    </Suspense>
                                                </AppLayout>
                                            )}
                                            // Need exact prop to be true
                                            // This ensures subroutes that should not be rendered are not matched by parent routes
                                            exact
                                        />
                                        {
                                            // Header/footer layout with 404 page
                                            !isSignedIn && (
                                                <Route
                                                    exact
                                                    path={getRoutesWithLayout(
                                                                LAYOUT_HEADER_FOOTER
                                                            )
                                                                .map(({ allPaths }) => allPaths)
                                                                .flat()
                                                                .concat('*')
                                                        }
                                                    render={({ match }) => (
                                                        <HeaderFooterLayoutPages
                                                            match={match}
                                                            accountType={accountData.accountType}
                                                            logoutAction={logoutAction}
                                                            renderComponentWithRoute={renderComponentWithRoute}
                                                            include404
                                                        />
                                                    )}
                                                />
                                            )
                                        }
                                    </Switch>
                                    {/* <LiveChatIfApplicable /> */}
                                    </>
                                )
                            }
                        </ErrorBoundary>
                    </Suspense>
                </WhiteLabel>
            </ErrorBoundary>
        );
    } else if (!isMobileApp) {
        // Default to black loading spinner as we don't have white label styles yet
        // Make exception for VL and show pink
        return <Loading fill spinnerColour={/videoloft/.test(host) ? TURQUOISE : WHITE()} />;
    } else {
        // Don't want black loading spinner on mobile app as there are too many spinners already
        return null;
    }
});

/* ---------------------------- Helper components --------------------------- */

// Renders the LiveChat widget on every layout except the header/footer one
// const LiveChatIfApplicable = () => {
//     const { pathname } = useLocation();
//     if (isMobileApp || isUsingTouchscreen() || !/videoloft/.test(host)) {
//         return null;
//     }
//     const route = Object.values(routes).find(({ allPaths }) =>
//         allPaths.some(p => matchPath(pathname, { path: p, exact: true }))
//     );
//     const layoutType = route?.layout?.type ?? route?.layout;
//     // Routes with no layout field default to header/footer
//     if (!layoutType || layoutType === LAYOUT_HEADER_FOOTER) {
//         return null;
//     }
//     return <LiveChatWidget license="14616666" />;
// };

// Component to render instead if user is signed in and page cannot be accessed by a signed in user (i.e. redirectIfLoggedIn is true)
const LoggedInRedirect = ({ accountType }) => {
    const location = useLocation();
    const loggedInRedirect = getLoggedInRedirectInSessionStorage();
    const shouldCheckInstallerCustomers =
        accountType === 'i' &&
        !location.state?.from &&
        typeof loggedInRedirect !== 'string';
    const [installerHasNoCustomers, setInstallerHasNoCustomers] = useState(false);
    const [isLoadingInstallerCustomers, setIsLoadingInstallerCustomers] = useState(shouldCheckInstallerCustomers);

    useEffect(() => {
        let isMounted = true;

        const fetchInstallerCustomers = async () => {
            if (!shouldCheckInstallerCustomers) {
                setIsLoadingInstallerCustomers(false);
                setInstallerHasNoCustomers(false);
                return;
            }

            setIsLoadingInstallerCustomers(true);

            try {
                const [activeCustomers, suspendedCustomers] = await Promise.all([
                    getInstallerCustomers({ suspended: false }),
                    getInstallerCustomers({ suspended: true })
                ]);

                if (!isMounted) {
                    return;
                }

                const activeCount = Array.isArray(activeCustomers) ? activeCustomers.length : 0;
                const suspendedCount = Array.isArray(suspendedCustomers) ? suspendedCustomers.length : 0;

                setInstallerHasNoCustomers(activeCount + suspendedCount === 0);
            } catch {
                if (isMounted) {
                    setInstallerHasNoCustomers(false);
                }
            } finally {
                if (isMounted) {
                    setIsLoadingInstallerCustomers(false);
                }
            }
        };

        fetchInstallerCustomers();

        return () => {
            isMounted = false;
        };
    }, [shouldCheckInstallerCustomers]);
    
    // Redirect to first possible page out of the last page visited, portal, or cameras
    let pathname, search, hash, redirectTo;
    if (location.state?.from) {
        // Last page visited
        if (typeof location.state.from === 'string') {
            // If location.state.from is just a URL, split into pathname and search
            const split = location.state.from.split('?');
            pathname = split[0];
            if (split[1]) {
                search = '?' + split[1];
            }
        } else {
            // If location.state.from is an object, extract pathname and search
            pathname = location.state.from.pathname;
            search = location.state.from.search;
        }
    } else if (typeof loggedInRedirect === 'string') {
        // Not always possible to have `from` in current location state
        // We use session storage in these cases
        // This can cause issues though with stale values left in storage
        [pathname, search] = loggedInRedirect.split('?');
        if (search?.length > 0) {
            search = '?' + search;
        }
    } else if (
        installerHasNoCustomers &&
        routes.partnerPortal?.restrictAccess?.includes(accountType)
    ) {
        const partnerPortalStartPath =
            routes.partnerPortal.startPath ??
            `${routes.partnerPortal.path}${routes.partnerPortal.routes?.gettingStarted?.path ?? ''}`;
        const [startPathname, startSearch] = partnerPortalStartPath.split('?');
        pathname = startPathname;
        if (startSearch?.length > 0) {
            search = `?${startSearch}`;
        }
    } else if (
        accountType === 'i' &&
        routes.partnerPortal?.restrictAccess?.includes(accountType) &&
        routes.partnerPortal?.routes?.customers?.path
    ) {
        pathname = routes.partnerPortal.routes.customers.path;
    } else if (accountType === 'd') {
        redirectTo = '/manage/#';
    } else if (routes.portal?.restrictAccess.includes(accountType)) {
        // Portal
        // routes.portal may not exist if removed by white label
        pathname = routes.portal.path;
    } else {
        // Cameras
        // If on a takeaway white label, cameras won't exist and we want /orders instead
        pathname = routes.cameras?.path ?? routes.takeawayOrders?.path ?? '/';
    }

    useEffect(() => {
        // Clear session storage
        return clearLoggedInRedirectInSessionStorage;
    }, []);

    const shouldRedirectToLegacyManagePortal = redirectTo === MANAGE_PORTAL_PATH;

    useEffect(() => {
        if (!shouldRedirectToLegacyManagePortal) {
            return;
        }

        redirectToManagePortal();
    }, [shouldRedirectToLegacyManagePortal]);

    if (isLoadingInstallerCustomers) {
        return <Loading fill />;
    }

    if (shouldRedirectToLegacyManagePortal) {
        return null;
    }

    if (redirectTo) {
        return <Redirect to={redirectTo} />;
    }
    
    return (
        <Redirect
            to={{
                pathname,
                search,
                hash,
                state: {
                    from: location.pathname, // Store page redirected from in location state
                },
            }}
        />
    );
};

// Header/footer layout code gets duplicated in App component above as its position in tree changes depending on whether it needs to include 404 page or not
// This component minimises that duplication
const HeaderFooterLayoutPages = withTranslation()(({ match, accountType, logoutAction, renderComponentWithRoute, include404, t }) => {

    return (
        <HeaderFooterLayout
            accountType={
                accountType
            }
            logout={logoutAction}
            routeConfig={getRouteByPath(
                match.path
            )}
        >
            <ErrorBoundary
                errorComponent={ErrorScreen}
                message={t(
                    translationKeys.errors
                        .PAGE_ERROR
                )}
            >
                <Suspense
                    fallback={
                        <Loading
                            fill
                        />
                    }
                >
                    <Switch>
                        {getRoutesWithLayout(
                            LAYOUT_HEADER_FOOTER
                        ).map(
                            renderComponentWithRoute
                        )}

                        {
                            // 404 page
                            include404 && (
                                <Route>
                                    <NotFound />
                                </Route>
                            )
                        }
                        
                    </Switch>
                </Suspense>
            </ErrorBoundary>
        </HeaderFooterLayout>
    );
});

/* ---------------------------- Helper functions ---------------------------- */

export const getAllPathsThatMeetAccessCondition = (
    routeConfig, accountInfo
) => {
    if (
        !canUserAccessRoute(routeConfig, accountInfo)
    ) {
        return [];
    }

    const { path, acceptPaths, routes } = routeConfig;

    const allPaths = [path];

    if (acceptPaths) {
        allPaths.push(...acceptPaths);
    }

    if (routes) {
        for (const page in routes) {
            allPaths.push(
                ...getAllPathsThatMeetAccessCondition(
                    routes[page],
                    accountInfo
                )
            );
        }
    }

    return allPaths;
};


export default AppRouter;
